import React from 'react'
import { NextPage } from 'next'
import { ArticleDirectoryPage } from 'experiences/learn/articles/ArticleDirectory'
import { getQueryParam } from 'lib'
import { getSEOServersideProps, PagePropsWrapper } from 'lib/routing/nextPageUtils'
import { Article } from 'services/ContentfulService/models/articles'
import { Topic } from 'services/ContentfulService/models/topics'
import { fetchArticles } from 'services/ContentfulService/hooks/articles'
import { getTopicBySlug } from 'services/ContentfulService/hooks/topics'
import { Breadcrumb, Breadcrumbs } from 'shared/layout/CustomHead/Schemas/breadcrumbSchema'
import { StandardLayout } from 'shared/layout/StandardLayout'
import { TopicConstraint } from 'services/ContentfulService/constraints'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'

interface Props {
	articles: Article[]
	initialPage?: number
	topic: Topic
}

const PAGE_SIZE = 7
const LearnArticles: NextPage<PagePropsWrapper<Props>> = ({ metaTags, pageProps }) => {
	const breadcrumbs: Breadcrumb[] = [
		Breadcrumbs.Learn(),
		Breadcrumbs.Topic(pageProps.topic),
		{ name: `${pageProps.topic.name} Articles`, url: metaTags.url }
	]
	return (
		<StandardLayout metaTags={metaTags} schemas={[{ '@type': 'BreadcrumbList', breadcrumbs }]}>
			<ArticleDirectoryPage
				initialArticles={pageProps.articles}
				topic={pageProps.topic}
				initialPage={pageProps.initialPage}
			/>
		</StandardLayout>
	)
}

export const getServerSideProps = getSEOServersideProps<Props>(
	async ctx => {
		const { query } = ctx
		const initialTopic = query && query.topic ? getQueryParam(query.topic)[0] : undefined
		if (!initialTopic) {
			throw new ObjectNotFoundError('article')
		}
		let pageCount: number | undefined
		try {
			pageCount = parseInt(ctx.query.page as string)
		} catch (e) {}

		const topic = await getTopicBySlug(initialTopic)
		return {
			articles: await fetchArticles({
				constraints: topic ? [TopicConstraint(topic.id)] : [],
				count: PAGE_SIZE,
				offset: PAGE_SIZE * (pageCount || 0)
			}),
			initialPage: pageCount,
			topic: topic
		}
	},
	(metadataTags, pageProps) => {
		if (pageProps.topic) {
			const initialTopicLabel = pageProps.topic.name
			return {
				...metadataTags,
				title: `${initialTopicLabel} Articles`,
				description: `Articles about ${pageProps.topic.name}: ${pageProps.topic.subtitle}`
			}
		} else {
			return metadataTags
		}
	},
	'/learn'
)

export default LearnArticles
